import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';
import { Highlight, themes } from 'prism-react-renderer';
import LazyImage from '@/components/ui/LazyImage';

interface BlogContentProps {
  content: string;
}

export const BlogContent = ({ content }: BlogContentProps) => {
  // Create a custom sanitization schema that allows all common HTML tags and Schema.org attributes
  const customSchema = {
    ...defaultSchema,
    tagNames: [
      ...(defaultSchema.tagNames || []),
      'div', 'span', 'hr', 'article', 'section', 'nav', 'aside', 'header', 'footer', 'main',
      'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      'strong', 'em', 'b', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup',
      'br', 'wbr',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
      'figure', 'figcaption', 'picture', 'source',
      'details', 'summary',
      'meta', 'time'
    ],
    strip: ['script', 'iframe', 'object', 'embed', 'style', 'form'],
    attributes: {
      ...defaultSchema.attributes,
      '*': [
        ...(defaultSchema.attributes?.['*'] || []),
        'className', 'class', 'id',
        'itemprop', 'itemscope', 'itemtype', 'itemid'
      ],
      meta: ['itemprop', 'content', 'name', 'property'],
      a: ['href', 'title', 'target', 'rel', 'itemprop'],
      img: ['src', 'alt', 'title', 'width', 'height', 'loading', 'itemprop'],
      time: ['datetime', 'itemprop'],
      div: ['className', 'class', 'id', 'itemprop', 'itemscope', 'itemtype'],
      span: ['className', 'class', 'id', 'itemprop'],
      article: ['className', 'class', 'id', 'itemprop', 'itemscope', 'itemtype'],
      section: ['className', 'class', 'id', 'itemprop', 'itemscope', 'itemtype'],
      p: ['className', 'class', 'id', 'itemprop'],
      h1: ['className', 'class', 'id', 'itemprop'],
      h2: ['className', 'class', 'id', 'itemprop'],
      h3: ['className', 'class', 'id', 'itemprop'],
      h4: ['className', 'class', 'id', 'itemprop'],
      h5: ['className', 'class', 'id', 'itemprop'],
      h6: ['className', 'class', 'id', 'itemprop'],
      ul: ['className', 'class', 'id', 'itemprop'],
      ol: ['className', 'class', 'id', 'itemprop'],
      li: ['className', 'class', 'id', 'itemprop'],
    }
  };

  return (
    <div className="prose prose-lg max-w-none dark:prose-invert">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, customSchema]]}
        components={{
          // Custom code block with syntax highlighting
          code({ node, inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const code = String(children).replace(/\n$/, '');
            
            return !inline && match ? (
              <Highlight theme={themes.oneDark} code={code} language={match[1]}>
                {({ className, style, tokens, getLineProps, getTokenProps }) => (
                  <pre className={`${className} rounded-lg p-4 overflow-x-auto my-4`} style={style}>
                    {tokens.map((line, i) => (
                      <div key={i} {...getLineProps({ line })}>
                        {line.map((token, key) => (
                          <span key={key} {...getTokenProps({ token })} />
                        ))}
                      </div>
                    ))}
                  </pre>
                )}
              </Highlight>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          // Custom image component with lazy loading
          img({ node, ...props }: any) {
            return (
              <LazyImage
                src={props.src || ''}
                alt={props.alt || ''}
                className="rounded-lg my-4"
              />
            );
          },
          // Custom link component (opens external links in new tab)
          a({ node, ...props }: any) {
            const isExternal = props.href?.startsWith('http');
            return (
              <a
                {...props}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="text-primary hover:underline"
              />
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
